import { X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { cn } from '@/lib/utils';

import type { CreateSlotParams, Interviewer } from '@/types/scheduling.types';

interface SlotCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (params: CreateSlotParams) => Promise<void>;
  interviewers: Interviewer[];
  initialDate?: string;
  initialStartTime?: string;
  initialEndTime?: string;
}

const slotSchema = z
  .object({
    interviewerUserId: z.string().min(1),
    date: z.string().min(1),
    startTime: z.string().min(1),
    endTime: z.string().min(1),
    capacity: z.number().min(1),
    notes: z.string().optional(),
  })
  .refine((data) => data.endTime > data.startTime, {
    message: 'endAfterStart',
    path: ['endTime'],
  });

interface SlotFormData {
  interviewerUserId: string;
  date: string;
  startTime: string;
  endTime: string;
  capacity: number;
  notes: string;
}

const labelClassName = 'block text-sm font-medium text-gray-700';
const inputClassName =
  'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

function buildInitialFormData(initialDate?: string, initialStartTime?: string, initialEndTime?: string): SlotFormData {
  return {
    interviewerUserId: '',
    date: initialDate ?? '',
    startTime: initialStartTime ?? '',
    endTime: initialEndTime ?? '',
    capacity: 1,
    notes: '',
  };
}

export function SlotCreateModal({
  isOpen,
  onClose,
  onSave,
  interviewers,
  initialDate,
  initialStartTime,
  initialEndTime,
}: SlotCreateModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<SlotFormData>(() =>
    buildInitialFormData(initialDate, initialStartTime, initialEndTime),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(buildInitialFormData(initialDate, initialStartTime, initialEndTime));
      setErrors({});
    }
  }, [isOpen, initialDate, initialStartTime, initialEndTime]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setErrors({});

      const result = slotSchema.safeParse(formData);
      if (!result.success) {
        const fieldErrors: Record<string, string> = {};
        for (const issue of result.error.issues) {
          const path = issue.path.join('.');
          fieldErrors[path] = t(`scheduling.slotCreate.validation.${issue.message}`, issue.message);
        }
        setErrors(fieldErrors);
      } else {
        setIsSubmitting(true);
        try {
          const startAtUtc = new Date(`${result.data.date}T${result.data.startTime}:00`).toISOString();
          const endAtUtc = new Date(`${result.data.date}T${result.data.endTime}:00`).toISOString();
          await onSave({
            interviewerUserId: result.data.interviewerUserId,
            startAtUtc,
            endAtUtc,
            capacity: result.data.capacity,
            notes: result.data.notes ?? undefined,
          });
        } finally {
          setIsSubmitting(false);
        }
      }
    },
    [formData, onSave, t],
  );

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
      <div className='mx-4 flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl'>
        <div className='flex items-center justify-between border-b border-gray-200 px-6 py-4'>
          <h2 className='text-lg font-semibold text-gray-900'>{t('scheduling.slotCreate.title')}</h2>
          <button onClick={onClose} className='rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600'>
            <X className='h-5 w-5' />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className='flex-1 overflow-y-auto px-6 py-4'>
          <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
            {/* Interviewer */}
            <div className='sm:col-span-2'>
              <label className={labelClassName}>{t('scheduling.slotCreate.interviewer')}</label>
              <select
                value={formData.interviewerUserId}
                onChange={(e) => setFormData((prev) => ({ ...prev, interviewerUserId: e.target.value }))}
                className={cn(inputClassName, errors['interviewerUserId'] && 'border-red-500')}>
                <option value=''>{t('scheduling.slotCreate.interviewerPlaceholder')}</option>
                {interviewers.map((interviewer) => (
                  <option key={interviewer.userId} value={interviewer.userId}>
                    {interviewer.name}
                  </option>
                ))}
              </select>
              {errors['interviewerUserId'] && (
                <p className='mt-1 text-xs text-red-600'>{errors['interviewerUserId']}</p>
              )}
            </div>

            {/* Date */}
            <div className='sm:col-span-2'>
              <label className={labelClassName}>{t('scheduling.slotCreate.date')}</label>
              <input
                type='date'
                value={formData.date}
                onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
                className={cn(inputClassName, errors['date'] && 'border-red-500')}
              />
              {errors['date'] && <p className='mt-1 text-xs text-red-600'>{errors['date']}</p>}
            </div>

            {/* Start Time */}
            <div>
              <label className={labelClassName}>{t('scheduling.slotCreate.startTime')}</label>
              <input
                type='time'
                value={formData.startTime}
                onChange={(e) => setFormData((prev) => ({ ...prev, startTime: e.target.value }))}
                className={cn(inputClassName, errors['startTime'] && 'border-red-500')}
              />
              {errors['startTime'] && <p className='mt-1 text-xs text-red-600'>{errors['startTime']}</p>}
            </div>

            {/* End Time */}
            <div>
              <label className={labelClassName}>{t('scheduling.slotCreate.endTime')}</label>
              <input
                type='time'
                value={formData.endTime}
                onChange={(e) => setFormData((prev) => ({ ...prev, endTime: e.target.value }))}
                className={cn(inputClassName, errors['endTime'] && 'border-red-500')}
              />
              {errors['endTime'] && <p className='mt-1 text-xs text-red-600'>{errors['endTime']}</p>}
            </div>

            {/* Capacity */}
            <div>
              <label className={labelClassName}>{t('scheduling.slotCreate.capacity')}</label>
              <input
                type='number'
                min={1}
                value={formData.capacity}
                onChange={(e) => setFormData((prev) => ({ ...prev, capacity: Number(e.target.value) }))}
                className={cn(inputClassName, errors['capacity'] && 'border-red-500')}
              />
              {errors['capacity'] && <p className='mt-1 text-xs text-red-600'>{errors['capacity']}</p>}
            </div>

            {/* Notes */}
            <div className='sm:col-span-2'>
              <label className={labelClassName}>{t('scheduling.slotCreate.notes')}</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className={inputClassName}
                placeholder={t('scheduling.slotCreate.notesPlaceholder')}
              />
            </div>
          </div>
        </form>

        <div className='flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4'>
          <button
            type='button'
            onClick={onClose}
            disabled={isSubmitting}
            className='rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50'>
            {t('scheduling.slotCreate.cancel')}
          </button>
          <button
            type='submit'
            disabled={isSubmitting}
            onClick={(e) => void handleSubmit(e as unknown as React.FormEvent)}
            className='rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'>
            {isSubmitting ? t('scheduling.slotCreate.saving') : t('scheduling.slotCreate.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
